import axiosInstance from '../../../core/api/interceptors/axiosInterceptor';

export const getCustomers = async (page = 1, pageSize = 20, searchTerm = '', filterType = 'all') => {
  const params = { page, pageSize };
  
  // Add search term if provided
  if (searchTerm && searchTerm.trim()) {
    params.search = searchTerm.trim();
  }
  
  // Add filter type if not 'all'
  if (filterType && filterType !== 'all') {
    params.filterType = filterType;
  }
  
  const response = await axiosInstance.get('Customer/get-customers', { params });
  return response.data;
};

export const getCustomerById = async (id) => {
  const response = await axiosInstance.get(`Customer/get-customer/${id}`);
  return response.data;
};

export const addCustomer = async (customer) => {
  const response = await axiosInstance.post('Customer/add-customer', customer);
  return response.data;
};

export const updateCustomer = async (id, customer) => {
  const response = await axiosInstance.put(`Customer/update-customer/${id}`, customer);
  return response.data;
};

export const deleteCustomer = async (id) => {
  await axiosInstance.delete(`Customer/delete-customer/${id}`);
};

export const lookupCompanyByVAT = async (vatNumber) => {
  const response = await axiosInstance.get(`Company/lookup-cbe/${vatNumber}`);
  return response.data;
};

// Search customers by name, email, VAT, or phone (single searchTerm param)
export const searchCustomersByTerm = async (searchTerm) => {
  const params = searchTerm && searchTerm.trim() ? { searchTerm: searchTerm.trim() } : {};
  const response = await axiosInstance.get('Customer/search', { params });
  return response.data;
};

/**
 * Detect the type of customer search input and return appropriate query params.
 * - VAT number: starts with country code (2 letters) followed by digits, or is "BE0" / similar patterns
 * - Email: contains '@'
 * - Otherwise: treated as name
 */
const detectSearchType = (inputValue) => {
  const trimmed = inputValue.trim();
  
  // Email detection: contains @
  if (trimmed.includes('@')) {
    return { email: trimmed };
  }
  
  // VAT number detection:
  // - Starts with 2-letter country code followed by digits (e.g. BE0123456789, NL123456789B01)
  // - Or starts with 0 followed by digits (Belgian format without country prefix)
  if (/^[A-Za-z]{2}\d/.test(trimmed) || /^0\d{8,}$/.test(trimmed)) {
    return { vatNumber: trimmed };
  }
  
  // Default: search by name
  return { searchTerm: trimmed };
};

export const searchCustomers = async (inputValue) => {
  if (!inputValue || inputValue.trim().length < 2) return [];

  const params = detectSearchType(inputValue);

  try {
    const response = await axiosInstance.get('customer/search', { params });    
    // Extract payload from response
    const customers = response.data.payload || response.data || [];
    
    // Map backend response to frontend format
    return customers.map((c) => ({
      Id: c.Id,
      Name: c.Name,
      CompanyName: c.CompanyName,
      Email: c.Email,
      Phone: c.PhoneNumber || c.MobilePhone,
      PhoneNumber: c.PhoneNumber,
      MobileNumber: c.MobilePhone,
      VATNumber: c.VATNumber,
      Street: c.Street,
      Box: c.Box,
      PostCode: c.PostCode,
      City: c.City,
      Country: c.Country,
      MobilePhone: c.MobilePhone,
      CompanyId: c.CompanyId,
      IsActive: c.IsActive,
      CustomerPeppolId: c.CustomerPeppolId,
      CreatedDate: c.CreatedDate,
      IsDeleted: c.IsDeleted,
      ProfilePicture: c.ProfilePicture || c.Photo,
      // For select display
      value: c.Id,
      label: c.Name || c.CompanyName || 'Unknown',
    }));
  } catch (error) {
    throw error;
  }
};

/**
 * Get outstanding documents for a customer
 */
export const getCustomerOutstandingDocuments = async (customerId) => {
  try {
    const response = await axiosInstance.get(`Customer/${customerId}/documents`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Create a customer payment
 */
export const createCustomerPayment = async (paymentData) => {
  try {
    const response = await axiosInstance.post('Customer/payment', paymentData);
    return response.data;
  } catch (error) {
    throw error;
  }
};